import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import * as actions from '../../../redux/actions';
import api from '../../../utils/functions/api';
import { filterMarketingEmployees } from '../utils/employee';
import { formatEmployeeThaiName } from '../../../utils/functions/employeeName';

const EMPTY_FORM = {
  opportunityNumber: '',
  code: '',
  xxx: '',
  projectName: '',
  detail: '',
  isTender: false,
  type: '',
  typeOther: '',
  status: 'WAITING',
  remark: '',
  note: '',
  budget: '',
  quotationDueDate: '',
};

export function useOpportunityForm({ open, opportunity, onSaved, onClose }) {
  const dispatch = useDispatch();
  const contact = useSelector((state) => state.contact);
  const employee = useSelector((state) => state.employee);
  const isEdit = !!opportunity?.id;

  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactInput, setContactInput] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerInput, setOwnerInput] = useState('');
  const [selectedOrderedBy, setSelectedOrderedBy] = useState(null);
  const [orderedByInput, setOrderedByInput] = useState('');
  // นัดหมาย — เพิ่ม/ลบทีละรายการยิงตรงเลย (ไม่รวมกับ handleSave) เพราะต้องมี opportunity.id
  // อยู่แล้วถึงจะเพิ่มได้ (แก้ไขเท่านั้น ตอนสร้างใหม่กรอกวันที่ไว้ก่อน ส่งพร้อม payload ตอนสร้าง)
  const [appointments, setAppointments] = useState([]);
  const [newAppointmentDate, setNewAppointmentDate] = useState('');
  const [newAppointmentTitle, setNewAppointmentTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        opportunityNumber: opportunity.opportunity_number || '',
        code: opportunity.code || '',
        projectName: opportunity.project_name || '',
        detail: opportunity.detail || '',
        isTender: !!opportunity.is_tender,
        type: opportunity.type || '',
        typeOther: opportunity.type_other || '',
        remark: opportunity.remark || '',
        note: opportunity.note || '',
        budget: opportunity.budget ?? '',
        quotationDueDate: opportunity.quotation_due_date
          ? dayjs(opportunity.quotation_due_date).format('YYYY-MM-DD') : '',
      });
      setSelectedContact(
        opportunity.contact_id
          ? { _id: opportunity.contact_id, name: opportunity.contact_name_snapshot || '' }
          : null,
      );
      // owner_id เป็น loose reference ไป Mongo Employee (คนละ DB กับ opportunity เอง) —
      // ไม่มี name snapshot เก็บไว้เหมือน contact ต้อง fetch ชื่อจริงแยกต่างหาก ใช้ผลลัพธ์ตรงจาก
      // API เรียกเองแทนการพึ่ง state.employee (redux slice เดียวกับที่ ownerOptions ใช้ค้นหาอยู่แล้ว
      // ด้านล่าง — ถ้า employeeAll resolve ทีหลัง employeeGet จะทับข้อมูลกันได้ ดู gotcha state.project
      // เดิมใน CLAUDE.md)
      if (opportunity.owner_id) {
        setSelectedOwner({ _id: opportunity.owner_id, name: '...' });
        api.get(`${process.env.REACT_APP_API_URL}/employee/${opportunity.owner_id}`)
          .then(({ data }) => {
            setSelectedOwner({ _id: opportunity.owner_id, name: formatEmployeeThaiName(data) });
          })
          .catch(() => {});
      } else {
        setSelectedOwner(null);
      }
      // ordered_by (ผู้ประสานงาน) — loose reference ไป Mongo Employee เหมือน owner_id เป๊ะ
      // (คนละ field แต่ pattern เดียวกัน: ไม่มี name snapshot ต้อง fetch ชื่อจริงแยกต่างหาก)
      if (opportunity.ordered_by) {
        setSelectedOrderedBy({ _id: opportunity.ordered_by, name: '...' });
        api.get(`${process.env.REACT_APP_API_URL}/employee/${opportunity.ordered_by}`)
          .then(({ data }) => {
            setSelectedOrderedBy({ _id: opportunity.ordered_by, name: formatEmployeeThaiName(data) });
          })
          .catch(() => {});
      } else {
        setSelectedOrderedBy(null);
      }
      setAppointments(opportunity.marketing_appointments || []);
    } else {
      setForm(EMPTY_FORM);
      setSelectedContact(null);
      setSelectedOwner(null);
      setSelectedOrderedBy(null);
      setAppointments([]);
    }
    setContactInput('');
    setOwnerInput('');
    setOrderedByInput('');
    setNewAppointmentDate('');
    setNewAppointmentTitle('');
    setError('');
  }, [open, isEdit, opportunity]);

  // ดึงลูกค้า/พนักงานทั้งหมดครั้งเดียวตอนเปิดฟอร์ม (ไม่ debounce ค้นหาทีละคำ+size:20 ต่อคำ
  // ค้นแบบเดิม — เปิด dropdown เฉยๆ ยังไม่พิมพ์อะไรก็เห็นแค่ 20 รายชื่อแรก) Autocomplete
  // กรอง client-side จาก options เต็มชุดเองอยู่แล้วจาก contactInput/ownerInput ไม่ต้องยิง
  // query ใหม่ทุกครั้งที่พิมพ์ — ดู pattern เดียวกันใน OpportunityList.js/useOpportunityDashboard.js
  useEffect(() => {
    if (!open) return;
    dispatch(actions.contactAll({ contactType: 'client', size: 9999, page: 1 }));
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;
    dispatch(actions.employeeAll({ size: 9999, page: 1, activeOnly: false }));
  }, [open, dispatch]);

  const contactOptions = useMemo(() => contact?.rows || [], [contact?.rows]);
  const ownerOptions = useMemo(() => filterMarketingEmployees(employee?.rows).map((e) => ({
    _id: e._id || e.id,
    name: formatEmployeeThaiName(e),
  })), [employee?.rows]);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // ตอนสร้างใหม่ (!isEdit) ยังไม่มี opportunity.id ให้ยิง API ทีละวัน — เก็บไว้ใน local state
  // ก่อน แล้วส่งพร้อม payload ตอนกด "บันทึก" ทีเดียว (ดู handleSave) ส่วนตอนแก้ไข ยิงทันทีเลย
  // เพราะ opportunity.id มีอยู่แล้ว
  const addAppointment = async () => {
    if (!newAppointmentDate) return;
    if (isEdit) {
      const created = await dispatch(actions.opportunityAddAppointment({
        id: opportunity.id, date: newAppointmentDate, title: newAppointmentTitle.trim() || undefined,
      }));
      setAppointments((p) => [...p, created].sort((a, b) => new Date(a.date) - new Date(b.date)));
    } else {
      setAppointments((p) => [...p, {
        id: `local-${Date.now()}`, date: newAppointmentDate, title: newAppointmentTitle.trim(),
      }].sort((a, b) => new Date(a.date) - new Date(b.date)));
    }
    setNewAppointmentDate('');
    setNewAppointmentTitle('');
  };

  const removeAppointment = async (appointmentId) => {
    if (isEdit) {
      await dispatch(actions.opportunityRemoveAppointment({ id: opportunity.id, appointmentId }));
    }
    setAppointments((p) => p.filter((a) => a.id !== appointmentId));
  };

  const handleSave = async () => {
    if (!form.projectName?.trim() || !selectedContact) {
      setError('กรุณาระบุชื่อโครงการและลูกค้า');
      return;
    }
    if (!isEdit && !/^[A-Za-z]{2}$/.test(form.code?.trim() || '')) {
      setError('กรุณาเลือก Code');
      return;
    }
    if (!isEdit && !/^[0-9]{4}$/.test(form.xxx?.trim() || '')) {
      setError('กรุณาระบุเลขที่เอกสาร 4 หลัก เช่น 0035');
      return;
    }
    if (form.type === 'OTHER' && !form.typeOther?.trim()) {
      setError('กรุณาระบุรายละเอียดประเภทงานเมื่อเลือก Other');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        projectName: form.projectName.trim(),
        contactId: selectedContact._id || selectedContact.id,
        contactNameSnapshot: selectedContact.name,
        detail: form.detail || undefined,
        isTender: form.isTender,
        type: form.type || undefined,
        typeOther: form.type === 'OTHER' ? form.typeOther.trim() : undefined,
        remark: form.remark || undefined,
        note: form.note || undefined,
        ownerId: selectedOwner?._id || selectedOwner?.id || undefined,
        orderedById: selectedOrderedBy?._id || selectedOrderedBy?.id || undefined,
        budget: form.budget === '' ? undefined : Number(form.budget),
        quotationDueDate: form.quotationDueDate || undefined,
      };
      if (isEdit) {
        await dispatch(actions.updateOpportunity({ id: opportunity.id, ...payload }));
      } else {
        await dispatch(actions.createOpportunity({
          ...payload,
          code: form.code.trim().toUpperCase(),
          xxx: form.xxx.trim(),
          status: form.status,
          appointments: appointments.map((a) => ({ date: a.date, title: a.title || undefined })),
        }));
      }
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  return {
    isEdit,
    form,
    setField,
    selectedContact,
    setSelectedContact,
    contactInput,
    setContactInput,
    contactOptions,
    selectedOwner,
    setSelectedOwner,
    ownerInput,
    setOwnerInput,
    ownerOptions,
    selectedOrderedBy,
    setSelectedOrderedBy,
    orderedByInput,
    setOrderedByInput,
    appointments,
    newAppointmentDate,
    setNewAppointmentDate,
    newAppointmentTitle,
    setNewAppointmentTitle,
    addAppointment,
    removeAppointment,
    saving,
    error,
    handleSave,
  };
}

export default useOpportunityForm;
